import { IndexedDBCache } from "finhelper"
import {Api} from "laravel-request";
import moment from "moment";
import {call, put, select, takeLatest} from 'redux-saga/effects'

import {getSingleUserAccount} from "#app/api";
import * as interfaceActions from "#app/store/actions/interface";
import type {CustomTypeFormActionPayload, FetchTriggerActionPayload} from "#app/store/actions/interface";

interface TriggerAction {
    payload?: FetchTriggerActionPayload | null;
}

interface CustomTypeAction {
    payload: CustomTypeFormActionPayload;
}

function* _getApplications(action: TriggerAction) {
    try {
        const promise = new Promise((resolve, reject) => {
            Api.get('crm-contact', 'index')
                .with('requisite')
                .with('requisite_bank')
                .with('files')
                .all((response) => {
                    resolve(response);
                }, () => {
                    reject()
                });
        });

        const response = yield promise;

        yield put(interfaceActions.handleContacts(response));

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* getContacts(action: TriggerAction) {
    try {
        const promise = new Promise((resolve, reject) => {
            Api.get('crm-contact', 'index')
                .with('requisite')
                .with('requisite_bank')
                .with('files')
                .all((response) => {
                    resolve(response);
                }, () => {
                    reject()
                });
        });

        const response = yield promise;

        yield put(interfaceActions.handleContacts(response));

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* getContactById(action: any) {
    try {
        const contactId = action.payload.id;

        const promise = new Promise((resolve, reject) => {
            Api.get('crm-contact', 'index')
                .where('id', contactId)
                .with('requisite')
                .with('requisite_bank')
                .with('files')
                .all((response) => {
                    resolve(response);
                }, () => {
                    reject()
                });
        });

        const response: any = yield promise;


        yield put(interfaceActions.handleContactById({id: contactId, data: response?.data}));

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* getClients(action: TriggerAction) {
    try {
        const promise = new Promise((resolve, reject) => {
            Api.get('client', 'index').orderBy('id', 'ASC').all(
                (response) => {
                    resolve(response);
                }, (_response) => {
                    reject();
                }
            )
        });

        const response = yield promise;

        yield put(interfaceActions.handleClients(response as any));

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* getCurrencies(action: TriggerAction) {
    try {

        const cachePromise = IndexedDBCache.get("currencies");

        let data: any = yield cachePromise;

        if (!data) {
            const promise = new Promise((resolve, reject) => {
                Api.get('currency', 'index')
                    .setDomain(process.env.REACT_APP_API_WHITESWAN_URL ?? '')
                    .with('cb_currency')
                    .orderBy('order')
                    .all((response) => {
                        resolve(response);
                    }, (_response) => {
                        reject();
                    })
            })

            const response: any = yield promise;

            data = response.data;

            IndexedDBCache.set('currencies', data, 1000 * 60 * 60 * 24)
        }

        yield put(interfaceActions.handleCurrencies(data));

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* getUserAccounts(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                getSingleUserAccount(
                    state.interfaceComponents.client.id,
                    resolve,
                    reject
                );
            });

            const response = yield promise;
            yield put(interfaceActions.handleUserAccounts(response as any));
        } else {
            yield put(interfaceActions.handleUserAccounts({data: []}));
        }

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* getCourses(action: TriggerAction) {
    try {
        const now = moment().format('YYYY-MM-DD');

        const cachePromise = IndexedDBCache.get("courses_" + now);

        let data: any = yield cachePromise;

        if (!data) {
            const promise = new Promise((resolve, reject) => {
                Api.get('cb-history-currency-course', 'latest')
                    .setDomain(process.env.REACT_APP_API_WHITESWAN_URL ?? '')
                    .all((response) => {
                        resolve(response)
                    }, (_xhr) => {
                        reject();
                    });
            });

            const response: any = yield promise;

            data = response.data;

            IndexedDBCache.set('courses_' + now, data, 1000 * 60 * 60 * 24)
        }

        yield put(interfaceActions.handleCourses(data));

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* getSpendTypes(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active', 'spending-types', {
                    user_id: state.interfaceComponents.client.id,
                    all: true
                }).call((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handleSpendTypes(response as any));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* getIncomeTypes(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active', 'income-types', {
                    user_id: state.interfaceComponents.client.id,
                    all: true
                }).call((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handleIncomeTypes(response as any));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* getActiveTags(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active-tag', 'index', {
                    user_id: state.interfaceComponents.client.id,
                }).all((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handleActiveTags(response));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* getActiveSubcategories(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active-subcategory', 'index', {
                    user_id: state.interfaceComponents.client.id
                }).all((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handleActiveSubcategories(response));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* getPropertyTypes(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active', 'property-types', {
                    user_id: state.interfaceComponents.client.id
                }).all((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handlePropertyTypes(response as any));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* getInvestTypes(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active', 'invest-types', {
                    user_id: state.interfaceComponents.client.id
                }).all((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handleInvestTypes(response as any));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* getGroupTypes(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active', 'group-types', {
                    user_id: state.interfaceComponents.client.id
                }).all((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handleGroupTypes(response as any));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* getClassTypes(action: TriggerAction) {
    try {
        const state: any = yield select();

        if (state.interfaceComponents.client) {
            const promise = new Promise((resolve, reject) => {
                Api.get('active', 'class-types', {
                    user_id: state.interfaceComponents.client.id
                }).all((response) => {
                    resolve(response)
                }, () => {
                    reject();
                });
            });

            const response = yield promise;

            yield put(interfaceActions.handleClassTypes(response as any));

            if (action.payload && typeof action.payload.callback === "function") {
                yield call(action.payload.callback);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

function* postCustomGroupType(action: CustomTypeAction) {
    try {
        const promise = new Promise((resolve, reject) => {
            Api.post('active-custom-group-type', 'store', action.payload.form)
                .withoutNotify()
                .call((response) => {
                    action.payload.success?.(response);

                    resolve(response)
                }, (xhr, responseData) => {
                    action.payload.error?.(xhr, responseData);

                    reject();
                });
        });

        const _response = yield promise;

        yield put(interfaceActions.getGroupTypes());

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* postCustomClassType(action: CustomTypeAction) {
    try {
        const promise = new Promise((resolve, reject) => {
            Api.post('active-custom-class-type', 'store', action.payload.form)
                .withoutNotify()
                .call((response) => {
                    action.payload.success?.(response);

                    resolve(response)
                }, (xhr, responseData) => {
                    action.payload.error?.(xhr, responseData);

                    reject();
                });
        });

        const _response = yield promise;

        yield put(interfaceActions.getClassTypes());

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* postCustomType(action: CustomTypeAction) {
    try {
        const promise = new Promise((resolve, reject) => {
            Api.post('active-custom-type', 'store', action.payload.form)
                .withoutNotify()
                .call((response) => {
                    action.payload.success?.(response);

                    resolve(response)
                }, (xhr, responseData) => {
                    action.payload.error?.(xhr, responseData);

                    reject();
                });
        });

        const _response = yield promise;

        yield put(interfaceActions.getSpendTypes());
        yield put(interfaceActions.getIncomeTypes());
        yield put(interfaceActions.getActiveTags());
        yield put(interfaceActions.getActiveSubcategories());
        yield put(interfaceActions.getPropertyTypes());
        yield put(interfaceActions.getInvestTypes());

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* putCustomType(action: CustomTypeAction) {
    try {
        const promise = new Promise((resolve, reject) => {
            Api.putArg('active-custom-type', 'update', action.payload.form.id, action.payload.form)
                .withoutNotify()
                .call((response) => {
                    action.payload.success?.(response);

                    resolve(response)
                }, (xhr, responseData) => {
                    action.payload.error?.(xhr, responseData);

                    reject();
                });
        });

        const _response = yield promise;

        yield put(interfaceActions.getSpendTypes());
        yield put(interfaceActions.getIncomeTypes());
        yield put(interfaceActions.getActiveTags());
        yield put(interfaceActions.getActiveSubcategories());
        yield put(interfaceActions.getPropertyTypes());
        yield put(interfaceActions.getInvestTypes());

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}


export default function* watcher() {
    yield takeLatest(interfaceActions.getCurrencies, getCurrencies)
    yield takeLatest(interfaceActions.getClients, getClients)
    yield takeLatest(interfaceActions.getUserAccounts, getUserAccounts)
    yield takeLatest(interfaceActions.getCourses, getCourses)
    yield takeLatest(interfaceActions.getSpendTypes, getSpendTypes)
    yield takeLatest(interfaceActions.getIncomeTypes, getIncomeTypes)
    yield takeLatest(interfaceActions.getActiveTags, getActiveTags)
    yield takeLatest(interfaceActions.getActiveSubcategories, getActiveSubcategories)
    yield takeLatest(interfaceActions.getPropertyTypes, getPropertyTypes)
    yield takeLatest(interfaceActions.getInvestTypes, getInvestTypes)
    yield takeLatest(interfaceActions.getGroupTypes, getGroupTypes)
    yield takeLatest(interfaceActions.getClassTypes, getClassTypes)
    yield takeLatest(interfaceActions.postCustomType, postCustomType)
    yield takeLatest(interfaceActions.putCustomType, putCustomType)
    yield takeLatest(interfaceActions.postCustomGroupType, postCustomGroupType)
    yield takeLatest(interfaceActions.postCustomClassType, postCustomClassType)
    yield takeLatest(interfaceActions.getContacts, getContacts)
    yield takeLatest(interfaceActions.getContactById, getContactById)
}
